import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CubeIcon } from '@heroicons/react/24/outline';
import { FaEthereum } from 'react-icons/fa';
import ImmutableMintButton from './ImmutableMintButton';

export default function NFTMintModal({ isOpen, onClose, onMint, collection }) {
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState('mint'); // 'mint', 'processing', 'success'

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setIsProcessing(false);
      setStep('mint');
    }
  }, [isOpen]);

  const handleMint = async () => {
    setIsProcessing(true);
    setStep('processing');

    // Simulate minting process
    setTimeout(() => {
      setStep('success');
      setIsProcessing(false);
    }, 3000);

    // Call the actual mint function
    await onMint(quantity);
  };

  const totalPrice = (collection?.price || 0) * quantity;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !isProcessing && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-dark-lighter border border-violet-500/20 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-bold leading-6 text-white flex items-center"
                  >
                    <CubeIcon className="h-5 w-5 mr-2 text-violet-500" />
                    {step === 'mint' && 'Mint NFT'}
                    {step === 'processing' && 'Processing...'}
                    {step === 'success' && 'Mint Successful!'}
                  </Dialog.Title>
                  {!isProcessing && (
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  )}
                </div>

                {step === 'mint' && (
                  <>
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-white font-semibold">{collection?.name}</h4>
                          <p className="text-gray-400 text-sm">
                            {collection?.mintedCount} / {collection?.totalSupply} minted
                          </p>
                        </div>
                        <div className="flex items-center text-white">
                          <FaEthereum className="mr-1" />
                          <span>{collection?.price} per NFT</span>
                        </div>
                      </div>

                      <div className="bg-dark-300 rounded-lg p-4 mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-300">Quantity</span>
                          <div className="flex items-center">
                            <button
                              onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-dark-400 text-white hover:bg-violet-600 transition-colors"
                              disabled={quantity <= 1}
                            >
                              -
                            </button>
                            <span className="mx-3 text-white font-semibold">{quantity}</span>
                            <button
                              onClick={() => quantity < 10 && setQuantity(quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-dark-400 text-white hover:bg-violet-600 transition-colors"
                              disabled={quantity >= 10}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="h-2 bg-dark-400 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-violet-500 to-purple-600"
                            style={{ width: `${(collection?.mintedCount / collection?.totalSupply) * 100}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="bg-dark-300 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-300">Price per NFT</span>
                          <div className="flex items-center text-white">
                            <FaEthereum className="mr-1" />
                            <span>{collection?.price}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-300">Quantity</span>
                          <span className="text-white">{quantity}</span>
                        </div>
                        <div className="border-t border-gray-700 my-2 pt-2"></div>
                        <div className="flex justify-between items-center">
                          <span className="text-white font-semibold">Total</span>
                          <div className="flex items-center text-white font-semibold">
                            <FaEthereum className="mr-1" />
                            <span>{totalPrice.toFixed(3)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      {collection?.draftNFTs && collection.draftNFTs.length > 0 ? (
                        <ImmutableMintButton
                          nft={collection.draftNFTs[0]}
                          onSuccess={(updatedNft) => {
                            setStep('success');
                            setIsProcessing(false);
                            if (onMint) onMint(quantity);
                          }}
                          className="glow-button w-full"
                        />
                      ) : (
                        <button
                          onClick={handleMint}
                          className="glow-button w-full"
                        >
                          Mint Now
                        </button>
                      )}
                    </div>
                  </>
                )}

                {step === 'processing' && (
                  <div className="text-center py-8">
                    <div className="flex justify-center mb-4">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-violet-500"></div>
                    </div>
                    <p className="text-white mb-2">Processing your transaction...</p>
                    <p className="text-gray-400 text-sm">Please wait while we mint your NFT(s)</p>
                  </div>
                )}

                {step === 'success' && (
                  <div className="text-center py-6">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 bg-violet-500/20 rounded-full flex items-center justify-center">
                        <svg className="w-10 h-10 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <h4 className="text-xl font-bold text-white mb-2">NFT Minted Successfully!</h4>
                    <p className="text-gray-400 mb-6">You have successfully minted {quantity} NFT(s)</p>
                    <button
                      onClick={onClose}
                      className="glow-button w-full"
                    >
                      View My NFTs
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
